import { useContext } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { GapBox } from 'components/Box'
import { BoxForm, Slider, Switch } from 'components/Form'
import { SectionHeader } from 'components/Header'
import Paper from 'components/Paper'
import { AddEditDomainContext } from 'features/inventory/domain/contexts/AddEditDomainContext'
import { useAuthUser } from 'features/user'
import { useAbility } from 'providers/casl'
import ProductAdUnitsTable from '../../components/ProductAdUnitsTable'
import { useTrafficPercentMarks } from './hooks/useTrafficPercentMarks'
import { Schema } from './schema'

export const Form = (): React.JSX.Element => {
  const ability = useAbility()
  const { t } = useTranslation('features/domain', { keyPrefix: 'form.priceGenius' })
  const {
    helpers: { getProductStatus },
  } = useAuthUser()
  const { domain } = useContext(AddEditDomainContext)
  const { control } = useFormContext<Schema>()

  if (!domain) {
    throw Error('Domain should be accessible')
  }

  const trafficPercentMarks = useTrafficPercentMarks()

  const cannotUpdateDomain: boolean = ability.cannot('update', 'Domain')
  const cannotUpdatePriceGenius: boolean = ability.cannot('update', 'Product-priceGenius')

  const isSendFloorsToPrebidFloorPriceModuleDisabled: boolean =
    cannotUpdateDomain || getProductStatus('prebidStack') === 'inactive'

  return (
    <>
      <Paper>
        <SectionHeader
          subtitle={t('enabled.description')}
          title={t('enabled.title')}
          titleEndAdornment={
            <Switch
              control={control}
              disabled={cannotUpdateDomain || cannotUpdatePriceGenius}
              name='priceGeniusEnabled'
            />
          }
        />

        <GapBox />

        <BoxForm>
          <Slider
            control={control}
            disabled={cannotUpdateDomain}
            label={t('trafficPercent.title')}
            marks={trafficPercentMarks}
            name='trafficPercent'
            tooltip={{ content: t('trafficPercent.tooltip') }}
            withInput
          />

          <Switch
            control={control}
            disabled={isSendFloorsToPrebidFloorPriceModuleDisabled}
            label={t('sendFloorsToPrebidFloorPriceModule.label')}
            labelPlacement='start'
            name='sendFloorsToPrebidFloorPriceModule'
            tooltip={{
              content: isSendFloorsToPrebidFloorPriceModuleDisabled
                ? t('sendFloorsToPrebidFloorPriceModule.hint')
                : t('sendFloorsToPrebidFloorPriceModule.tooltip'),
            }}
            tooltipMode={isSendFloorsToPrebidFloorPriceModuleDisabled ? 'wrap' : 'icon'}
          />
        </BoxForm>
      </Paper>

      <Controller
        control={control}
        name='adUnitsAttributes'
        render={() => (
          <ProductAdUnitsTable
            domainId={domain.id}
            product='priceGenius'
          />
        )}
      />
    </>
  )
}
